import * as AWS from 'aws-sdk/global';
import S3 from 'aws-sdk/clients/s3';
import { gql, Resolver } from '@apollo/client';

interface DownloadFromS3Args {
    bucketName: string;
    bucketRegion: string;
    identityPoolId: string;
    key: string;
}

/**
 * Download an object from S3.
 *
 * Note that there appears to be an implicit rate limit on calling s3.getObject as we are doing
 * below, based on an uncaught error that includes `TooManyRequests` on the identity pool. It's hard
 * to find documentation on what that rate limit is, but 5 requests per second (one per 200 ms)
 * seems like a safe rate to use, based on experimentation.
 * @param obj
 * @param args
 */
export const downloadFromS3: Resolver = async (
    obj: Record<string, unknown>,
    args: DownloadFromS3Args,
): Promise<DownloadResult> => {
    const { bucketName, bucketRegion, identityPoolId, key } = args;

    const credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId,
    });

    AWS.config.update({
        region: bucketRegion,
        credentials,
        retryDelayOptions: {
            base: 1000,
        },
    });

    const s3 = new S3({
        params: { Bucket: bucketName },
        region: bucketRegion,
        credentials,
    });

    const params = {
        Bucket: bucketName,
        Key: key,
    };

    const results: DownloadResult = {
        key,
        raw: null,
        parsed: null,
    };

    await s3
        .getObject(params)
        .promise()
        .then((data: S3.GetObjectOutput) => {
            const responseBody =
                (data.Body as Uint8Array) ?? new TextEncoder().encode('');

            if (key.match(/^.*\.(json|JSON)$/)) {
                results.parsed = JSON.parse(
                    new TextDecoder('utf-8').decode(responseBody),
                );
            } else {
                results.raw = responseBody;
            }
        })
        .catch(err => {
            Promise.reject(err);
        });

    return Promise.resolve(results);
};

export const DOWNLOAD_FROM_S3 = gql`
    query DownloadFromS3(
        $bucketName: String
        $bucketRegion: String
        $identityPoolId: String
        $key: String
    ) {
        downloadFromS3(
            bucketName: $bucketName
            bucketRegion: $bucketRegion
            identityPoolId: $identityPoolId
            key: $key
        ) @client
    }
`;
